import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import { viewPDF } from "../../utils/viewPDF";
import SuccessModal from "../../utils/SuccessModal";
import * as XLSX from "xlsx";
import ViewAllBidsTable from "./ViewAllBidsTable";
import AllocationSection from "./AllocationSection";
import ViewAllottedBids from "./ViewAllottedBids";
import SummaryTable from "./SummaryTable";
import ViewAllBidsTableExport from "./ViewAllBidsTableExport";
import { useDownloadExcel } from "react-export-table-to-excel";
import ViewAllBidsTableNew from "./ViewAllBidsTableNew";
import AllBidsTableForPDF from "./AllBidsTableForPDF";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { handleExportPDF } from "../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ViewAllBids = () => {
  const { rfqId } = useParams();
  const [bidData, setBidData] = useState();
  const [groupedData, setGroupedData] = useState();
  const [rfq, setRfq] = useState();
  const [loading, setLoading] = useState(true);
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [comment, setComment] = useState();
  const [rebidData, setRebidData] = useState();
  const [openRebidModal, setOpenRebidModal] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [allocations, setAllocations] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [applyValue, setApplyValue] = useState(false);
  const [openAuction, setOpenAuction] = useState(false);
  const [auctionFile, setAuctionFile] = useState();
  const tableRef = useRef(null);
  const awardRef = useRef();
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: "Quotes Comparison",
    sheet: "Quotes Comparison",
  });

  const handleChange = (e) => {
    setRebidData({ ...rebidData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const getBidData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/bid?username=${storedUser.username}&rfqId=${rfqId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      const allBids = res.data.filter(Boolean);
      setBidData(res.data);
      setGroupedData(groupBidsByVendor(allBids));
      setApplyValue(false);
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };
  const getRFQ = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/rfq/get-by-id?rfqId=${rfqId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setRfq(res.data);
    } catch (error) {
      console.error("Error updating tender status:", error);
    }
  };

  useEffect(() => {
    getRFQ();
    getBidData();
  }, [applyValue]);

  useEffect(() => {
    const getComment = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/comment?rfqId=${rfqId}&username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
        setComment(res.data);
      } catch (error) {
        console.error("Error updating tender status:", error);
      }
    };
    getComment();
  }, [rfq]);

  const groupBidsByVendor = (bids) => {
    const grouped = {};
    bids.forEach((bid) => {
      const name = bid.vendor.name;
      const firmName = bid.vendor.firmName;
      if (!grouped[`${name}-${firmName}`]) {
        grouped[`${name}-${firmName}`] = [];
      }
      grouped[`${name}-${firmName}`].push(bid);
    });
    return grouped;
  };

  const groupedWorkItems =
    rfq &&
    rfq.rfqWorkItems.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});

  const validateForm = (formType = "") => {
    const newErrors = {};
    const data = rebidData || {};
    const now = new Date();

    const isEmpty = (value) => !value || value.trim?.() === "";

    const startDate = data.rebidStartDate
      ? new Date(data.rebidStartDate)
      : null;
    const endDate = data.rebidEndDate ? new Date(data.rebidEndDate) : null;

    if (formType === "reasonForm") {
      if (isEmpty(reason)) newErrors.reason = "Reason is required";
    }

    if (formType === "rebidForm") {
      if (!startDate) {
        newErrors.rebidStartDate = "Rebid Start Date & Time is required";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate < today) {
          newErrors.rebidStartDate =
            "Rebid Start Date & Time cannot be in the past";
        }
      }

      if (!endDate) {
        newErrors.rebidEndDate = "Rebid End Date & Time is required";
      } else if (endDate < now) {
        newErrors.rebidEndDate = "Rebid End Date & Time cannot be in the past";
      } else if (startDate && endDate <= startDate) {
        newErrors.rebidEndDate =
          "Rebid End Date & Time must be after Start Date & Time";
      }

      if (isEmpty(data.rebidComment))
        newErrors.rebidComment = "Revised Quote Comment is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateBidStatus = async () => {
    if (!validateForm("reasonForm")) return;
    setBtnLoading(true);
    try {
      const bidsByVendor = bidData.reduce((acc, bid) => {
        const vendorId = bid.vendor.vendorId;
        if (!acc[vendorId]) {
          acc[vendorId] = [];
        }
        acc[vendorId].push(bid);
        return acc;
      }, {});
      const latestBids = Object.values(bidsByVendor).map((bidsArray) =>
        bidsArray.reduce((latest, current) => {
          return new Date(current.createdDate) > new Date(latest.createdDate)
            ? current
            : latest;
        })
      );
      const payload = latestBids.flatMap((bid) =>
        bid.bidWorkItems.map((b) => {
          const code = b.itemOrder;
          const vendorKey = `${bid.vendor.name}-${bid.vendor.firmName}`;
          const percentage = allocations[code]?.[vendorKey] || 0;
          return {
            bidId: b.bwId,
            rfqId: rfqId,
            username: storedUser.username,
            reason,
            status,
            percentageAllotted: percentage,
          };
        })
      );
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/bid`, payload, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });
      setOpen(false);
      navigate("/dashboard");
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdateBidStatusToRebid = async () => {
    if (!validateForm("rebidForm")) return;
    setBtnLoading(true);
    try {
      const payload = bidData.flatMap((bid) =>
        bid.bidWorkItems.map((b) => ({
          bidId: b.bwId,
          rfqId: rfqId,
          username: storedUser.username,
          status: "REBID",
          rebidStartDate: rebidData.rebidStartDate,
          rebidEndDate: rebidData.rebidEndDate,
          rebidComment: rebidData.rebidComment,
        }))
      );
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/bid`, payload, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });
      setOpenRebidModal(false);
      navigate("/dashboard");
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleCloseBids = async () => {
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/rfq/close-bid?username=${storedUser.username}&rfqId=${rfqId}`,
        {},
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        }
      );
      setOpenSuccess(true);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!auctionFile) {
      setErrors({ auctionFile: "File is required" });
      return;
    }
    setBtnLoading(true);

    const formData = new FormData();
    formData.append("file", auctionFile);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/eauction?rfqId=${rfqId}&endDate=${rebidData.rebidEndDate}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setOpenAuction(false);
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
    } finally {
      setBtnLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    const [hours, minutes] = time?.split(":") || [];
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... This may take a while. <CircularProgress color="#fff" />
        </div>
      )}
      {exportingExcel && (
        <div className="uploading-div">
          Exporting Excel... This may take a while. <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}
      <div style={{ display: "flex", justifyContent: "end", gap: 10 }}>
        <Button
          className="btn-contained"
          sx={{ width: "auto" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(awardRef, "Quotes_Comparison");
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export Comparatives PDF
        </Button>
        <Button
          className="btn-contained"
          sx={{ width: "auto" }}
          onClick={() => {
            try {
              setExportingExcel(true);
              onDownload();
            } catch (error) {
              console.log(error);
            } finally {
              setExportingExcel(false);
            }
          }}
        >
          Export Comparatives Excel
        </Button>
      </div>
      <Typography variant="h6">Quotes Received</Typography>
      <Box className="dashboard-container" py={10} px={2}>
        {storedUser.userRole[0] === "BUYER" && rfq && groupedData && (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              marginBottom: "30px",
              fontWeight: "bold",
            }}
          >
            Vendors Participated:{" "}
            {`${Object.keys(groupedData).length}/${rfq.totalVendors}`}
          </div>
        )}
        {comment &&
          comment.map((c) => (
            <Box
              key={c.id}
              className="dashboard-container"
              style={{
                background: "#F6F6F6",
                borderRadius: "0",
                padding: "10px 20px",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                Comment from: {c.orgUser?.name} ({formatDateTime(c.createdDate)})
              </Typography>
              <Typography pt={0}>{c.comment}</Typography>
            </Box>
          ))}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            Loading...
          </Box>
        ) : rfq && bidData && bidData.filter(Boolean).length > 0 ? (
          <>
            <SummaryTable
              rfq={rfq}
              groupedData={groupedData}
              bidData={bidData}
            />
            <ViewAllBidsTableNew
              rfq={rfq}
              bidData={bidData}
              groupedData={groupedData}
              groupedWorkItems={groupedWorkItems}
              storedUser={storedUser}
              applyValue={applyValue}
              setApplyValue={setApplyValue}
              getBidData={getBidData}
            />
            <div ref={tableRef} style={{ display: "none" }}>
              <ViewAllBidsTableExport
                rfq={rfq}
                bidData={bidData}
                groupedData={groupedData}
                groupedWorkItems={groupedWorkItems}
                storedUser={storedUser}
              />
            </div>
            <div
              ref={awardRef}
              style={{
                position: "absolute",
                top: "-9999px",
                left: "-9999px",
                height: "auto",
                width: "auto",
                paddingInline: "5px",
              }}
            >
              <ViewAllBidsTableExport
                rfq={rfq}
                bidData={bidData}
                groupedData={groupedData}
                groupedWorkItems={groupedWorkItems}
                storedUser={storedUser}
              />
            </div>
          </>
        ) : (
          <Typography variant="h6" textAlign="center" mt={5}>
            No vendor quotes for the RFQ
          </Typography>
        )}
        {storedUser.userRole[0] === "BUYER" && rfq && groupedData && (
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              marginTop: "30px",
              fontWeight: "bold",
            }}
          >
            Vendors Participated:{" "}
            {`${Object.keys(groupedData).length}/${rfq.totalVendors}`}
          </div>
        )}
      </Box>
      {rfq &&
      storedUser.userRole[0] === "BUYER" &&
      rfq.status === "BIDS_CLOSED" &&
      bidData &&
      bidData.filter(Boolean).length > 0 ? (
        <Box className="dashboard-container">
          <Typography>Allocate Percentage per Item & Vendor</Typography>
          <AllocationSection
            bidData={bidData}
            rfq={rfq}
            groupedData={groupedData}
            allocations={allocations}
            setAllocations={setAllocations}
            setIsValid={setIsValid}
          />
        </Box>
      ) : null}
      {rfq &&
        storedUser.userRole[0] !== "BUYER" &&
        rfq.status === "BIDS_CLOSED" && (
          <ViewAllottedBids
            rfq={rfq}
            bidData={bidData}
            groupedData={groupedData}
            groupedWorkItems={groupedWorkItems}
          />
        )}
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Box sx={{ display: "flex", gap: 5 }}>
          <Button
            className="btn-outlined"
            onClick={() => navigate(`/dashboard`)}
          >
            Back
          </Button>

          {storedUser.userRole[0] === "BUYER" &&
          rfq &&
          groupedData &&
          (new Date() <= new Date(rfq.submissionDate) ||
            !rfq.rebidEndDate ||
            new Date() <= new Date(rfq.rebidEndDate))
            ? rfq.status !== "BIDS_CLOSED" &&
              rfq.status !== "AWARDED" && (
                <Button
                  className="btn-contained"
                  onClick={handleCloseBids}
                  disabled={
                    btnLoading ||
                    rfq.totalVendors !== Object.keys(groupedData).length
                  }
                >
                  Close Bids
                </Button>
              )
            : null}

          {rfq && rfq.status === "BIDS_CLOSED" && rfq.status !== "AWARDED" && (
            <>
              {storedUser.userRole[0] === "BUYER" && rfq && groupedData && (
                <>
                  <Button
                    className="btn-outlined"
                    onClick={() => setOpenRebidModal(true)}
                    disabled={
                    !bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "SUBMITTED")
                    ) ||
                    !isValid ||
                    null
                  }
                  >
                    Request Revised RFQ
                  </Button>
                  <Button
                    className="btn-outlined"
                    onClick={() => setOpenAuction(true)}
                    disabled={
                    !bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "SUBMITTED")
                    ) ||
                    !isValid ||
                    null
                  }
                  >
                    E-Auction
                  </Button>
                </>
              )}
              {storedUser.userRole[0] === "BUYER" && (
                <Button
                  className="btn-contained"
                  onClick={() => {
                    setStatus("APPROVED_1");
                    setOpen(true);
                  }}
                  disabled={
                    !bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "SUBMITTED")
                    ) ||
                    !isValid ||
                    null
                  }
                >
                  Recommend
                </Button>
              )}
              {storedUser.userRole[0] !== "BUYER" &&
                ((storedUser.userRole[0] === "PURCHASE_MANAGER" &&
                bidData?.some((bid) =>
                  bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED_1")
                )) || 
              (bidData?.some(
                (bid) =>
                  bid?.awardee1 &&
                  bid?.awardee1?.userRoleName === storedUser.userRole[0]
              ) && bidData?.some((bid) =>
                  bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED_2")
                )) || 
              (bidData?.some(
                (bid) =>
                  bid?.awardee2 &&
                  bid?.awardee2?.userRoleName === storedUser.userRole[0]
              ) && bidData?.some((bid) =>
                  bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED")
                ))) && (
                  <Button
                    className="btn-outlined"
                    onClick={() => {
                      setStatus("SUBMITTED");
                      setOpen(true);
                    }}
                  >
                    Query to Buyer
                  </Button>
                )}
              {storedUser.userRole[0] === "PURCHASE_MANAGER" && (
                <Button
                  className="btn-contained"
                  onClick={() => {
                    setStatus("APPROVED_2");
                    setOpen(true);
                  }}
                  disabled={
                    !bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED_1")
                    ) || null
                  }
                >
                  Recommend
                </Button>
              )}
              {bidData?.some(
                (bid) =>
                  bid?.awardee1 &&
                  bid?.awardee1?.userRoleName === storedUser.userRole[0]
              ) && (
                <Button
                  className="btn-contained"
                  onClick={() => {
                    setStatus("APPROVED");
                    setOpen(true);
                  }}
                  disabled={
                    !bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED_2")
                    ) || null
                  }
                >
                  Recommend
                </Button>
              )}
              {bidData?.some(
                (bid) =>
                  bid?.awardee2 &&
                  bid.awardee2.userRoleName === storedUser.userRole[0]
              ) && (
                <Button
                  className="btn-contained"
                  onClick={() => {
                    setStatus("AWARDED");
                    setOpen(true);
                  }}
                  disabled={
                    bidData?.some((bid) =>
                      bid.bidWorkItems.some((b) => b.bidStatus === "AWARDED")
                    ) ||
                    (bidData?.some((bid) => bid.awardee1) &&
                      !bidData?.some((bid) =>
                        bid.bidWorkItems.some((b) => b.bidStatus === "APPROVED")
                      )) ||
                    (!bidData?.some((bid) => bid.awardee1) &&
                      !bidData?.some((bid) =>
                        bid.bidWorkItems.some(
                          (b) => b.bidStatus === "APPROVED_2"
                        )
                      ))
                  }
                >
                  Award
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      <Modal
        open={openAuction}
        onClose={() => setOpenAuction(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <label htmlFor="auctionFile">
            Please download the comparative using "Download As Excel" button and
            attach as file*
          </label>
          <input
            style={{ display: "block", padding: "20px" }}
            type="file"
            name="auctionFile"
            id="auctionFile"
            onChange={(e) => setAuctionFile(e.target.files[0])}
          />
          {errors.auctionFile && <p className="error">{errors.auctionFile}</p>}
          <label htmlFor="rebidEndDate">
            Revised Quote Submission End Date
          </label>
          <input
            type="datetime-local"
            name="rebidEndDate"
            id="rebidEndDate"
            onChange={handleChange}
            className={`${errors.rebidEndDate && "input-error"}`}
          />
          {errors.rebidEndDate && (
            <p className="error">{errors.rebidEndDate}</p>
          )}
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              onClick={() => setOpenAuction(false)}
            >
              Close
            </Button>
            <Button className="btn-contained" onClick={handleSendEmail}>
              Send Email
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            textAlign={"start"}
          >
            Enter your comments for{" "}
            {status === "SUBMITTED" ? "Reverting the Quotes" : "Recommendation"}
          </Typography>
          <textarea
            className={`${errors.reason && "input-error"}`}
            name="reason"
            id="reason"
            onChange={(e) => {
              setReason(e.target.value);
              setErrors({ ...errors, reason: "" });
            }}
            rows={8}
          ></textarea>
          {errors.reason && <p className="error">{errors.reason}</p>}
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button className="btn-contained" onClick={handleUpdateBidStatus}>
              {status === "SUBMITTED"
                ? "Send for Revision"
                : bidData?.some(
                    (bid) =>
                      bid?.awardee2 &&
                      bid.awardee2.userRoleName === storedUser.userRole[0]
                  )
                ? "Award"
                : "Recommend"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openRebidModal}
        onClose={() => setOpenRebidModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            textAlign={"start"}
          >
            Please Enter Valid Details for Revised Quote
          </Typography>
          <label htmlFor="rebidStartDate">
            Revised Quote Submission Start Date
          </label>
          <input
            type="datetime-local"
            name="rebidStartDate"
            id="rebidStartDate"
            onChange={handleChange}
            className={`${errors.rebidStartDate && "input-error"}`}
          />
          {errors.rebidStartDate && (
            <p className="error">{errors.rebidStartDate}</p>
          )}
          <label htmlFor="rebidEndDate">
            Revised Quote Submission End Date
          </label>
          <input
            type="datetime-local"
            name="rebidEndDate"
            id="rebidEndDate"
            onChange={handleChange}
            className={`${errors.rebidEndDate && "input-error"}`}
          />
          {errors.rebidEndDate && (
            <p className="error">{errors.rebidEndDate}</p>
          )}
          <label htmlFor="rebidComment">Revised Quote Comment</label>
          <input
            type="text"
            name="rebidComment"
            id="rebidComment"
            onChange={handleChange}
            className={`${errors.rebidComment && "input-error"}`}
          />
          {errors.rebidComment && (
            <p className="error">{errors.rebidComment}</p>
          )}
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              onClick={() => setOpenRebidModal(false)}
            >
              Close
            </Button>
            <Button
              className="btn-contained"
              onClick={handleUpdateBidStatusToRebid}
              disabled={btnLoading}
            >
              {btnLoading ? "Submitting" : "Send For Revision"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <SuccessModal
        open={openSuccess}
        handleClose={() => setOpenSuccess(false)}
        msg={"Quotes are now closed for this RFQ."}
        nav={"/dashboard"}
      />
    </div>
  );
};

export default ViewAllBids;